/**
 * Supabase client singleton for the doctor platform
 * 
 * This module provides a typed Supabase client instance with auth helpers,
 * session management utilities, and real-time subscription setup helpers.
 */

import { createClient, type SupabaseClient, type Session, type User } from '@supabase/supabase-js';
import type { Database } from '@/types/database.types';

// Environment configuration
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check your .env.local file contains:\n' +
    '- VITE_SUPABASE_URL\n' +
    '- VITE_SUPABASE_ANON_KEY'
  );
}

/**
 * Typed Supabase client instance
 */
export const supabase: SupabaseClient<Database> = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`,
    },
    realtime: {
      params: {
        eventsPerSecond: 10, // Throttle real-time events for better performance
      },
    },
    global: {
      headers: {
        'X-Client-Info': 'doctor-portal@1.0.0',
      },
    },
  }
);

// Types for auth helpers
export interface AuthUser {
  id: string;
  email: string | null;
  role: 'doctor' | 'admin' | null;
  emailConfirmed: boolean;
  createdAt: string;
  lastSignInAt: string | null;
}

export interface DoctorProfile {
  id: string;
  auth_user_id: string;
  full_name: string;
  medical_license: string;
  license_expiry: string | null;
  specialization: string[] | null;
  primary_practice_id: string | null;
  prescribing_enabled: boolean;
  require_2fa: boolean;
  verified_at: string | null;
  verified_by: string | null;
  last_prescription_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SessionInfo {
  session: Session;
  user: AuthUser;
  profile: DoctorProfile | null;
}

/**
 * Auth helper functions
 */
export const authHelpers = {
  /**
   * Get the current authenticated user
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error || !user) {
        return null;
      }

      return {
        id: user.id,
        email: user.email || null,
        role: (user.app_metadata?.role as 'doctor' | 'admin') || null,
        emailConfirmed: user.email_confirmed_at !== null,
        createdAt: user.created_at,
        lastSignInAt: user.last_sign_in_at || null,
      };
    } catch (error) {
      // Error handling: Failed to get current user
      return null;
    }
  },

  /**
   * Get current session information including user and profile
   */
  async getSessionInfo(): Promise<SessionInfo | null> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        return null;
      }

      const user: AuthUser = {
        id: session.user.id,
        email: session.user.email || null,
        role: (session.user.app_metadata?.role as 'doctor' | 'admin') || null,
        emailConfirmed: session.user.email_confirmed_at !== null,
        createdAt: session.user.created_at,
        lastSignInAt: session.user.last_sign_in_at || null,
      };

      // Get doctor profile if the user is a doctor
      let profile: DoctorProfile | null = null;
      if (user.role === 'doctor') {
        const { data: profileData } = await supabase
          .from('doctor_profiles')
          .select('*')
          .eq('auth_user_id', session.user.id)
          .single();
        
        profile = profileData;
      }

      return { session, user, profile };
    } catch (error) {
      // Error handling: Failed to get session info
      return null;
    }
  },

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return !!session;
    } catch (error) {
      // Error handling: Failed to check authentication
      return false;
    }
  },

  /**
   * Check if user has a specific role
   */
  async hasRole(role: 'doctor' | 'admin'): Promise<boolean> {
    try {
      const user = await this.getCurrentUser();
      return user?.role === role;
    } catch (error) {
      // Error handling: Failed to check user role
      return false;
    }
  },

  /**
   * Check if doctor is verified and can prescribe
   */
  async canPrescribe(): Promise<boolean> {
    try {
      const sessionInfo = await this.getSessionInfo();
      if (!sessionInfo || sessionInfo.user.role !== 'doctor' || !sessionInfo.profile) {
        return false;
      }

      const profile = sessionInfo.profile;
      const now = new Date();
      const licenseExpiry = profile.license_expiry ? new Date(profile.license_expiry) : null;
      
      return (
        profile.prescribing_enabled &&
        profile.verified_at !== null &&
        (!licenseExpiry || licenseExpiry > now)
      );
    } catch (error) {
      // Error handling: Failed to check prescribing ability
      return false;
    }
  },
};

/**
 * Session management utilities
 */
export const sessionManager = {
  /**
   * Listen to auth state changes
   */
  onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange(callback);
  },

  /**
   * Sign out user
   */
  async signOut(): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase.auth.signOut();
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  },

  /**
   * Refresh session
   */
  async refreshSession(): Promise<{ session: Session | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      return { session: data.session, error };
    } catch (error) {
      return { session: null, error: error as Error };
    }
  },

  /**
   * Get session with timeout
   */
  async getSessionWithTimeout(timeoutMs: number = 5000): Promise<Session | null> {
    try {
      const sessionPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Session fetch timeout')), timeoutMs)
      );

      const { data } = await Promise.race([sessionPromise, timeoutPromise]);
      return data.session;
    } catch (error) {
      // Error handling: Session fetch timeout or error
      return null;
    }
  },
};

/**
 * Real-time subscription helpers
 */
export const realtimeHelpers = {
  /**
   * Subscribe to doctor prescription changes
   */
  subscribeToDoctorPrescriptions(
    doctorId: string,
    callback: (payload: any) => void
  ) {
    return supabase
      .channel(`doctor-prescriptions:${doctorId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'doctor_prescriptions',
          filter: `doctor_id=eq.${doctorId}`,
        },
        callback
      )
      .subscribe();
  },

  /**
   * Subscribe to doctor prescription items changes
   */
  subscribeToDoctorPrescriptionItems(
    prescriptionId: string,
    callback: (payload: any) => void
  ) {
    return supabase
      .channel(`doctor-prescription-items:${prescriptionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'doctor_prescription_items',
          filter: `prescription_id=eq.${prescriptionId}`,
        },
        callback
      )
      .subscribe();
  },

  /**
   * Subscribe to doctor profile changes
   */
  subscribeToDoctorProfile(doctorId: string, callback: (payload: any) => void) {
    return supabase
      .channel(`doctor-profile:${doctorId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'doctor_profiles',
          filter: `auth_user_id=eq.${doctorId}`,
        },
        callback
      )
      .subscribe();
  },

  /**
   * Subscribe to all patients changes
   */
  subscribeToPatients(callback: (payload: any) => void) {
    return supabase
      .channel('patients')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'patients',
        },
        callback
      )
      .subscribe();
  },

  /**
   * Subscribe to medicines changes
   */
  subscribeToMedicines(callback: (payload: any) => void) {
    return supabase
      .channel('medicines')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'medicines',
        },
        callback
      )
      .subscribe();
  },

  /**
   * Unsubscribe from all channels
   */
  unsubscribeAll() {
    supabase.removeAllChannels();
  },

  /**
   * Unsubscribe from specific channel
   */
  unsubscribeFromChannel(channelName: string) {
    const channels = supabase.getChannels();
    const channel = channels.find((ch) => ch.topic === channelName);
    if (channel) {
      supabase.removeChannel(channel);
    }
  },
};

/**
 * Error handling utilities
 */
export const errorHandler = {
  /**
   * Check if error is authentication related
   */
  isAuthError(error: any): boolean {
    return (
      error?.status === 401 ||
      error?.code === 'PGRST301' ||
      error?.message?.includes('JWT') ||
      error?.message?.includes('auth')
    );
  },

  /**
   * Check if error is network related
   */
  isNetworkError(error: any): boolean {
    return (
      error?.code === 'NETWORK_ERROR' ||
      error?.message?.includes('fetch') ||
      error?.message?.includes('network') ||
      error?.name === 'TypeError'
    );
  },

  /**
   * Get user-friendly error message
   */
  getUserFriendlyMessage(error: any): string {
    if (this.isAuthError(error)) {
      return 'Authentication failed. Please sign in again.';
    }
    
    if (this.isNetworkError(error)) {
      return 'Network error. Please check your connection and try again.';
    }

    // Check for specific Supabase error codes
    switch (error?.code) {
      case '23505':
        return 'A record with this information already exists.';
      case '23503':
        return 'Cannot complete action due to data dependencies.';
      case '42501':
        return 'You do not have permission to perform this action.';
      case 'PGRST116':
        return 'The requested method is not allowed.';
      case '42P01':
        return 'The requested resource was not found.';
      default:
        return error?.message || 'An unexpected error occurred.';
    }
  },
};

// Default export
export default supabase;

// Re-export types for convenience
export type { Database } from '@/types/database.types';
export type { SupabaseClient, Session, User } from '@supabase/supabase-js';